// Facility slugs in this file identify entitlement coverage. Partner landing
// pages may opt into offer presentation separately through
// partnerFacilities.landing.freeAccess.offerKey.
//
// Cross-product user-facing copy for an offer lives in `copy` below.
// Components must NOT hardcode any copy or fallbacks — they compose strings
// from `copy` via `getOfferCopy`. Landing-page-specific copy belongs in
// partnerFacilities.landing.freeAccess and is adapter-injected by the partner
// loader only after an explicit offerKey match.
//
// Vocabulary (one name per concept, used the same way on every surface)
//
//   headline    Universal title for the offer ("Free email
//               notifications"). Reused on every surface so users see one
//               consistent name.
//   badge       One-word tag for compact UI (request list rows, history
//               items). Examples: "Sponsored", "On us", "Free".
//   reason      Why the notifications are free. Used as the body line in banners
//               and as the subtitle in the landing pill. Must be a complete
//               sentence on its own ("Sponsored by Surf Grove" /
//               "On us during Cyclone Narelle recovery"). Write the partner
//               name or goodwill context inline — every string is rendered
//               as-is, no template interpolation.
//   cta         Short prose tail appended to the reason in banners
//               ("We'll email you when a spot opens.").
//   trialZeroBanner
//               Banner shown on /browse when a user is out of trial
//               notifications but still has an active free-access request.
//   trialZeroAccountPrompt / trialPositiveAccountPrompt
//               Inline prompts on the account page nudging the user toward
//               a paid plan for non-offer campgrounds. Pick by trial balance.

const SURF_GROVE_FACILITY_SLUG = "tofino-bc-surf-grove-campground";
const FERNIE_RV_RESORT_FACILITY_SLUG = "fernie-bc-fernie-rv-resort";
const CAMP_VALHALLA_FACILITY_SLUG = "nakusp-bc-camp-valhalla";

export const FREE_ACCESS_OFFERS = [
  {
    key: "surf-grove-sponsored-email-alerts",
    enabled: true,
    facilitySlugs: [SURF_GROVE_FACILITY_SLUG],
    copy: {
      headline: "Free alerts",
      badge: "Sponsored",
      reason: "Sponsored by Surf Grove",
      cta: "We'll email you when a spot opens.",
      trialZeroBanner:
        "Your Surf Grove requests stay active. Subscribe to monitor other campgrounds.",
      trialZeroAccountPrompt:
        "Your Surf Grove requests stay active. Monitoring other campgrounds needs a subscription.",
      trialPositiveAccountPrompt:
        "Want to monitor other campgrounds beyond Surf Grove?",
    },
  },
  {
    key: "fernie-rv-resort-sponsored-email-alerts",
    enabled: true,
    facilitySlugs: [FERNIE_RV_RESORT_FACILITY_SLUG],
    copy: {
      headline: "Free alerts",
      badge: "Sponsored",
      reason: "Sponsored by Fernie RV Resort",
      cta: "We'll email you when a spot opens.",
      trialZeroBanner:
        "Your Fernie RV Resort requests stay active. Subscribe to monitor other campgrounds.",
      trialZeroAccountPrompt:
        "Your Fernie RV Resort requests stay active. Monitoring other campgrounds needs a subscription.",
      trialPositiveAccountPrompt:
        "Want to monitor other campgrounds beyond Fernie RV Resort?",
    },
  },
  {
    key: "camp-valhalla-sponsored-email-alerts",
    enabled: true,
    facilitySlugs: [CAMP_VALHALLA_FACILITY_SLUG],
    copy: {
      headline: "Free alerts",
      badge: "Sponsored",
      reason: "Sponsored by Camp Valhalla",
      cta: "We'll email you when a spot opens.",
      trialZeroBanner:
        "Your Camp Valhalla requests stay active. Subscribe to monitor other campgrounds.",
      trialZeroAccountPrompt:
        "Your Camp Valhalla requests stay active. Monitoring other campgrounds needs a subscription.",
      trialPositiveAccountPrompt:
        "Want to monitor other campgrounds beyond Camp Valhalla?",
    },
  },
];

export const REQUIRED_COPY_PATHS = [
  "headline",
  "badge",
  "reason",
  "cta",
  "trialZeroBanner",
  "trialZeroAccountPrompt",
  "trialPositiveAccountPrompt",
];

function toFacilitySlug(facilityOrSlug) {
  if (!facilityOrSlug) return null;
  if (typeof facilityOrSlug === "string") return facilityOrSlug;
  return facilityOrSlug.slug ?? facilityOrSlug.facilitySlug ?? null;
}

export function isOfferActive(offer) {
  return !!offer?.enabled;
}

export function validateOfferCopy(offer) {
  return REQUIRED_COPY_PATHS.filter((path) => {
    const value = offer.copy?.[path];
    return typeof value !== "string" || value.trim().length === 0;
  });
}

export function getActiveFacilityFreeAccessOffers(
  facilityOrSlug,
  { offers = FREE_ACCESS_OFFERS } = {},
) {
  const facilitySlug = toFacilitySlug(facilityOrSlug);
  if (!facilitySlug) return [];

  return offers
    .filter(isOfferActive)
    .filter((offer) => offer.facilitySlugs.includes(facilitySlug));
}

export function getActiveOfferForFacility(facilityOrSlug, options = {}) {
  return getActiveFacilityFreeAccessOffers(facilityOrSlug, options)[0] ?? null;
}

export function getConfiguredFacilityFreeAccessOffers(
  facilityOrSlug,
  { offers = FREE_ACCESS_OFFERS } = {},
) {
  const facilitySlug = toFacilitySlug(facilityOrSlug);
  if (!facilitySlug) return [];

  return offers.filter((offer) => offer.facilitySlugs.includes(facilitySlug));
}

export function getConfiguredOfferForFacility(facilityOrSlug, options = {}) {
  return (
    getConfiguredFacilityFreeAccessOffers(facilityOrSlug, options)[0] ?? null
  );
}

export function getActiveFreeAccessFacilitySlugs({
  offers = FREE_ACCESS_OFFERS,
} = {}) {
  return [
    ...new Set(offers.filter(isOfferActive).flatMap((o) => o.facilitySlugs)),
  ];
}

export function getConfiguredFreeAccessFacilitySlugs({
  offers = FREE_ACCESS_OFFERS,
} = {}) {
  return [...new Set(offers.flatMap((offer) => offer.facilitySlugs))];
}

export function isFreeAccessFacility(facilityOrSlug, options = {}) {
  return !!getActiveOfferForFacility(facilityOrSlug, options);
}

// Composes a flat copy bag for a single offer. UI surfaces and email templates
// read from this — no surface should reach into `offer.copy` directly or
// hardcode strings around it.
//
// The returned object also exposes pre-composed `body` (reason + cta) and
// `pill` (headline · reason) strings so consumers don't reassemble them.
export function getOfferCopy(offer) {
  if (!offer?.copy) return null;
  const c = offer.copy;
  const body = c.cta ? `${c.reason}. ${c.cta}` : `${c.reason}.`;
  const pill = `${c.headline} · ${c.reason}`;

  return {
    headline: c.headline,
    badge: c.badge,
    reason: c.reason,
    cta: c.cta,
    body,
    pill,
    landingValueProp: c.landingValueProp,
    landingCta: c.landingCta,
    trialZeroBanner: c.trialZeroBanner,
    trialZeroAccountPrompt: c.trialZeroAccountPrompt,
    trialPositiveAccountPrompt: c.trialPositiveAccountPrompt,
  };
}
